<template>
  <v-container fluid class="pa-0">
    <v-card class="mb-4 pa-4" outlined>
      <v-card-title class="pa-0 text-h6">Сохранённые отчёты маржинальности</v-card-title>
      <v-divider class="my-3" />
      <v-row>
        <v-col cols="12" md="3">
          <CustomSelect
            v-model="project"
            :items="filterOptions.projects"
            label="Проекты"
            item-text="name"
            item-value="id"
            chip-text="name"
            :loading="filterOptions.loading"
            :disabled="filterOptions.loading || !filterOptions.projects.length"
            multiple
          />
        </v-col>
        <v-col cols="12" md="3">
          <CustomSelect
            v-model="selectedPackages"
            :items="availablePackages"
            label="Пакеты"
            item-text="title"
            item-value="id"
            chip-text="title"
            :loading="filterOptions.loading"
            :disabled="filterOptions.loading || !availablePackages.length"
            multiple
          />
        </v-col>
        <v-col cols="12" md="3">
          <vc-date-picker
            :key="periodPickerKey"
            color="green"
            :is-dark="!!$vuetify.theme.dark"
            v-model="filterPeriod"
            :columns="$screens({ default: 1, md: 2, lg: 2 })"
            mode="date"
            is-range
            timezone="Europe/Moscow"
          >
            <template v-slot="{ inputValue, togglePopover }">
              <v-text-field
                readonly
                label="Период"
                class="period-filter-field"
                :disabled="!canFilter"
                :value="formatPeriodFilterInput(inputValue)"
                clearable
                @click="togglePopover()"
                @click:clear.stop="clearFilterPeriod"
              />
            </template>
          </vc-date-picker>
        </v-col>
        <v-col cols="12" md="3" class="d-flex align-center">
          <v-btn
            color="primary"
            small
            :disabled="!canFilter"
            :loading="loading"
            @click="loadReports"
          >
            Показать
          </v-btn>
        </v-col>
      </v-row>
    </v-card>

    <v-card v-if="reportsLoaded" outlined>
      <v-card-text>
        <div v-if="meta.last_page > 1" class="d-flex justify-center my-4">
          <v-pagination
            v-model="page"
            :length="meta.last_page"
            :total-visible="10"
            color="secondary"
            prev-icon="mdi-menu-left"
            next-icon="mdi-menu-right"
            @input="loadReports"
          />
        </div>

        <div class="table-wrap">
        <v-data-table
          :headers="headers"
          :items="reports"
          :loading="loading"
          loading-text="Загрузка…"
          class="elevation-0"
          dense
          hide-default-footer
          :items-per-page="reports.length || 15"
          no-data-text="Отчётов не найдено"
        >
          <template #item.period="{ item }">
            {{ formatPeriod(item.period) }}
          </template>
          <template #item.package="{ item }">
            {{ formatPackage(item) }}
          </template>
          <template #item.appendix="{ item }">
            {{ formatAppendix(item) }}
          </template>
          <template
            v-for="field in numericFields"
            #[`item.${field.key}`]="{ item }"
          >
            <span :key="field.key">{{ formatNumber(item[field.key]) }}</span>
          </template>
          <template #item.actions="{ item }">
            <div class="d-flex justify-center" style="gap: 2px">
              <v-tooltip bottom>
                <template #activator="{ on, attrs }">
                  <v-btn
                    v-bind="attrs"
                    x-small
                    color="info"
                    icon
                    v-on="on"
                    @click="onEdit(item)"
                  >
                    <v-icon small>mdi-pencil</v-icon>
                  </v-btn>
                </template>
                <span>Редактировать</span>
              </v-tooltip>
              <v-tooltip bottom>
                <template #activator="{ on, attrs }">
                  <v-btn
                    v-bind="attrs"
                    x-small
                    color="error"
                    icon
                    v-on="on"
                    @click="onDelete(item)"
                  >
                    <v-icon small>mdi-delete</v-icon>
                  </v-btn>
                </template>
                <span>Удалить</span>
              </v-tooltip>
            </div>
          </template>
        </v-data-table>
        </div>

        <div v-if="meta.last_page > 1" class="d-flex justify-center my-4">
          <v-pagination
            v-model="page"
            :length="meta.last_page"
            :total-visible="10"
            color="secondary"
            prev-icon="mdi-menu-left"
            next-icon="mdi-menu-right"
            @input="loadReports"
          />
        </div>
      </v-card-text>
    </v-card>

    <div class="saved-margin-reports-fab">
      <v-tooltip left>
        <template #activator="{ on, attrs }">
          <v-btn
            v-bind="attrs"
            color="success"
            dark
            fab
            v-on="on"
            @click="onCreate"
          >
            <v-icon>mdi-plus</v-icon>
          </v-btn>
        </template>
        <span>Новый отчёт</span>
      </v-tooltip>
    </div>

    <v-dialog v-model="deleteDialog" max-width="400">
      <v-card>
        <v-card-title>Удалить отчёт?</v-card-title>
        <v-card-text>Отчёт №{{ reportToDelete && reportToDelete.id }} будет удалён без возможности восстановления.</v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="deleteDialog = false">Отмена</v-btn>
          <v-btn color="error" :loading="deleting" @click="confirmDelete">Удалить</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import moment from 'moment'
import { mapActions } from 'vuex'

const TABLE_COLUMNS = [
  { key: 'period', label: 'Даты', sortable: false },
  { key: 'package', label: 'Пакет', sortable: false },
  { key: 'appendix', label: 'Приложение', sortable: false },
  { key: 'minute_cost', label: 'Цена минуты телефонии', numeric: true },
  { key: 'administrative_expenses_percent', label: 'Админ расходы', numeric: true },
  { key: 'operator_payroll', label: 'ФОТ операторов', numeric: true },
  { key: 'pm_payroll', label: 'ФОТ МП', numeric: true },
  { key: 'gl_payroll', label: 'ФОТ РГ', numeric: true },
  { key: 'overage_minutes', label: 'Превышение мин', numeric: true },
  { key: 'overage_rub', label: 'Превышение руб.', numeric: true },
  { key: 'total_duration_min', label: 'Общая длительность пом.', numeric: true },
  { key: 'talk_duration_min', label: 'Результативная длительность пом.', numeric: true },
  { key: 'talk_duration_under_15_min', label: 'Результативная длительность до 14 сек. пом.', numeric: true },
  { key: 'paid_minutes', label: 'Оплачиваемые минуты', numeric: true },
  { key: 'total_calls', label: 'Вызовов осуществлено', numeric: true },
  { key: 'paid_minute_cost', label: 'Себестоимость оплачиваемой минуты', numeric: true },
  { key: 'margin_excluding_admin_expenses', label: 'Маржинальность без админ. расходов', numeric: true },
  { key: 'margin_excluding_admin_expenses_percent', label: 'Маржинальность без админ. расходов %', numeric: true },
  { key: 'margin', label: 'Маржинальность', numeric: true },
  { key: 'margin_percent', label: 'Маржинальность%', numeric: true },
]

export default {
  name: 'SavedMarginReportsList',

  data() {
    return {
      numericFields: TABLE_COLUMNS.filter((column) => column.numeric),
      project: [],
      selectedPackages: [],
      filterPeriod: null,
      periodPickerKey: 0,
      filterOptions: {
        projects: [],
        loading: false,
      },
      reports: [],
      reportsLoaded: false,
      loading: false,
      deleting: false,
      page: 1,
      perPage: 15,
      meta: {
        last_page: 1,
      },
      deleteDialog: false,
      reportToDelete: null,
      headers: [
        ...TABLE_COLUMNS.map((column) => ({
          text: column.label,
          value: column.key,
          sortable: column.sortable !== false,
          align: column.numeric ? 'right' : undefined,
        })),
        { text: 'Действия', value: 'actions', align: 'center', sortable: false },
      ],
    }
  },

  computed: {
    canFilter() {
      return this.project.length > 0 || this.selectedPackages.length > 0
    },

    availablePackages() {
      const projects = this.project.length
        ? this.filterOptions.projects.filter((p) => this.project.includes(p.id))
        : this.filterOptions.projects

      const seenIds = new Set()
      return projects.flatMap((p) => p.packages || []).filter((pkg) => {
        const id = Number(pkg.id)
        if (seenIds.has(id)) return false
        seenIds.add(id)
        return true
      }).map((pkg) => ({
        ...pkg,
        id: Number(pkg.id),
      }))
    },
  },

  watch: {
    project() {
      this.syncSelectedPackages()
      this.page = 1
      this.reports = []
      this.reportsLoaded = false
    },

    selectedPackages() {
      this.page = 1
      this.reports = []
      this.reportsLoaded = false
    },

    filterPeriod: {
      handler() {
        this.page = 1
        this.reports = []
        this.reportsLoaded = false
      },
      deep: true,
    },
  },

  mounted() {
    this.loadFilterOptions()
  },

  methods: {
    ...mapActions({
      fetchFilterOptions: 'otm/saved_margin_reports/fetchFilterOptions',
      fetchReports: 'otm/saved_margin_reports/fetchReports',
      deleteReport: 'otm/saved_margin_reports/deleteReport',
    }),

    notifyError(message) {
      this.$store.commit('shared/SET_ERROR', { message }, { root: true })
    },

    notifySuccess(message) {
      this.$store.commit('shared/SET_SUCCESS', { message }, { root: true })
    },

    formatPeriod(period) {
      if (!Array.isArray(period) || period.length < 2) return '—'
      return `${period[0]} — ${period[1]}`
    },

    formatPackage(item) {
      return item.package_title
        || item.package_name
        || (item.package && item.package.title)
        || (item.package != null ? `Пакет №${item.package}` : '—')
    },

    formatAppendix(item) {
      return item.appendix_title
        || item.appendix_name
        || (item.appendix && item.appendix.title)
        || (item.appendix != null ? `Приложение №${item.appendix}` : '—')
    },

    formatNumber(value) {
      if (value == null || value === '') return '—'
      return value
    },

    formatPeriodFilterInput(inputValue) {
      if (inputValue && inputValue.start && inputValue.end) {
        return `${inputValue.start} - ${inputValue.end}`
      }
      return ''
    },

    clearFilterPeriod() {
      this.filterPeriod = null
      this.periodPickerKey += 1
    },

    syncSelectedPackages() {
      const availableIds = new Set(this.availablePackages.map((pkg) => pkg.id))
      this.selectedPackages = this.selectedPackages.filter((id) => availableIds.has(Number(id)))
    },

    buildPeriodParam() {
      if (!this.filterPeriod || !this.filterPeriod.start) return null

      const from = moment(this.filterPeriod.start).format('YYYY-MM-DD')
      const to = this.filterPeriod.end
        ? moment(this.filterPeriod.end).format('YYYY-MM-DD')
        : from

      return [from, to]
    },

    async loadFilterOptions() {
      this.filterOptions.loading = true
      try {
        const projects = await this.fetchFilterOptions()
        this.filterOptions.projects = projects.map((item) => ({
          ...item,
          id: Number(item.id),
          packages: (item.packages || []).map((pkg) => ({
            ...pkg,
            id: Number(pkg.id),
          })),
        }))
        this.syncSelectedPackages()
      } catch (err) {
        this.filterOptions.projects = []
        this.notifyError('Ошибка загрузки фильтров')
        // eslint-disable-next-line no-console
        console.error(err)
      } finally {
        this.filterOptions.loading = false
      }
    },

    async loadReports() {
      if (!this.canFilter) return

      this.loading = true
      try {
        const payload = {
          page: this.page,
          perPage: this.perPage,
          period: this.buildPeriodParam(),
        }

        if (this.project.length) {
          payload.projects = this.project
        }

        if (this.selectedPackages.length) {
          payload.packages = this.selectedPackages
        }

        const response = await this.fetchReports(payload)
        this.reports = response.data || []
        this.meta = response.meta || { last_page: 1 }
        this.reportsLoaded = true
      } catch (err) {
        this.reportsLoaded = false
        this.notifyError('Ошибка загрузки отчётов')
        // eslint-disable-next-line no-console
        console.error(err)
      } finally {
        this.loading = false
      }
    },

    onEdit(item) {
      this.$router.push({
        name: 'otm.saved-margin-reports.edit',
        params: { id: String(item.id) },
      })
    },

    onCreate() {
      this.$router.push({ name: 'otm.calculator' })
    },

    onDelete(item) {
      this.reportToDelete = item
      this.deleteDialog = true
    },

    async confirmDelete() {
      if (!this.reportToDelete) return

      this.deleting = true
      try {
        const response = await this.deleteReport(this.reportToDelete.id)
        this.notifySuccess((response && response.message) || 'Отчёт удалён!')
        this.deleteDialog = false
        this.reportToDelete = null
        await this.loadFilterOptions()
        if (this.canFilter) {
          await this.loadReports()
        } else {
          this.reports = []
          this.reportsLoaded = false
        }
      } catch (err) {
        const message = (err.response && err.response.data && err.response.data.message) || 'Ошибка удаления отчёта'
        this.notifyError(message)
      } finally {
        this.deleting = false
      }
    },
  },
}
</script>

<style scoped>
.saved-margin-reports-fab {
  position: fixed;
  right: 18px;
  bottom: 18px;
  z-index: 4;
}

.table-wrap {
  overflow-x: auto;
}

.period-filter-field >>> input {
  cursor: pointer;
}
</style>
